import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, 
  Upload,
  Download,
  CheckCircle,
  AlertCircle,
  User,
  Database,
  Trash2,
  Edit,
  Eye,
  RefreshCw,
  Clock,
  Filter,
  ArrowRight
} from 'lucide-react';

export type ActivityType = 'upload' | 'process' | 'edit' | 'delete' | 'export' | 'login' | 'system' | 'error';

interface ActivityEvent {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  user?: string;
  timestamp: Date;
  metadata?: {
    entity_type?: string;
    entity_id?: string;
    entity_name?: string;
    details?: Record<string, any>;
  };
  status: 'success' | 'error' | 'warning' | 'info';
}

const ActivityFeed: React.FC = () => {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ActivityType | 'all'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Generate realistic activity events
  const generateActivityEvents = (): ActivityEvent[] => {
    const now = new Date();
    const users = ['أحمد محمد', 'فاطمة الشهري', 'عبدالله العتيبي', 'سارة القحطاني'];
    const merchants = ['كارفور', 'لولو هايبرماركت', 'العثيم', 'الدانوب'];
    
    const events: ActivityEvent[] = [
      {
        id: '1',
        type: 'process',
        title: 'معالجة عرض جديد',
        description: 'تم إنتهاء معالجة عرض كارفور الأسبوعي - استخراج 28 منتج بنجاح',
        user: users[0],
        timestamp: new Date(now.getTime() - 5 * 60 * 1000), // 5 minutes ago
        metadata: {
          entity_type: 'offer',
          entity_id: '1234',
          entity_name: 'عرض كارفور الأسبوعي',
          details: {
            products_extracted: 28,
            processing_time: 45,
            confidence_avg: 0.92
          }
        },
        status: 'success'
      },
      {
        id: '2',
        type: 'upload',
        title: 'رفع ملفات جديدة',
        description: 'تم رفع 5 صور لعرض لولو هايبرماركت الجديد',
        user: users[1],
        timestamp: new Date(now.getTime() - 15 * 60 * 1000), // 15 minutes ago
        metadata: {
          entity_type: 'offer',
          entity_id: '1235',
          entity_name: 'عرض لولو الشتوي',
          details: {
            files_count: 5,
            total_size: '15.2 MB'
          }
        },
        status: 'info'
      },
      {
        id: '3',
        type: 'edit',
        title: 'تعديل بيانات منتج',
        description: 'تم تحديث سعر منتج "أرز بسمتي 5 كيلو" في عرض العثيم',
        user: users[2],
        timestamp: new Date(now.getTime() - 30 * 60 * 1000), // 30 minutes ago
        metadata: {
          entity_type: 'product',
          entity_id: '789',
          entity_name: 'أرز بسمتي 5 كيلو',
          details: {
            old_price: 25.50,
            new_price: 23.99,
            merchant: 'العثيم'
          }
        },
        status: 'info'
      },
      {
        id: '4',
        type: 'error',
        title: 'فشل في معالجة صورة',
        description: 'فشل في استخراج المنتجات من الصورة "offer_page_3.jpg" - جودة الصورة منخفضة',
        user: users[0],
        timestamp: new Date(now.getTime() - 45 * 60 * 1000), // 45 minutes ago
        metadata: {
          entity_type: 'image',
          entity_id: 'img_567',
          entity_name: 'offer_page_3.jpg',
          details: {
            error_code: 'LOW_QUALITY',
            confidence_score: 0.34
          }
        },
        status: 'error'
      },
      {
        id: '5',
        type: 'export',
        title: 'تصدير البيانات',
        description: 'تم تصدير قائمة المنتجات إلى ملف Excel بنجاح',
        user: users[3],
        timestamp: new Date(now.getTime() - 60 * 60 * 1000), // 1 hour ago
        metadata: {
          entity_type: 'export',
          entity_id: 'exp_123',
          details: {
            format: 'Excel',
            records_count: 456,
            file_size: '2.1 MB'
          }
        },
        status: 'success'
      },
      {
        id: '6',
        type: 'system',
        title: 'تحديث النظام',
        description: 'تم تحديث نموذج YOLO إلى الإصدار الجديد مع تحسينات في الدقة',
        timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000), // 2 hours ago
        metadata: {
          entity_type: 'system',
          details: {
            version: 'v2.1.3',
            improvements: 'accuracy +5%'
          }
        },
        status: 'success'
      },
      {
        id: '7',
        type: 'delete',
        title: 'حذف عرض منتهي الصلاحية',
        description: 'تم حذف عرض الدانوب المنتهي الصلاحية تلقائياً',
        timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000), // 3 hours ago
        metadata: {
          entity_type: 'offer',
          entity_id: '1200',
          entity_name: 'عرض الدانوب الصيفي',
          details: {
            reason: 'expired',
            products_count: 15
          }
        },
        status: 'warning'
      },
      {
        id: '8',
        type: 'login',
        title: 'تسجيل دخول جديد',
        description: 'تسجيل دخول المستخدم من عنوان IP جديد',
        user: users[1],
        timestamp: new Date(now.getTime() - 4 * 60 * 60 * 1000), // 4 hours ago
        metadata: {
          entity_type: 'auth',
          details: {
            ip_address: '192.168.1.100',
            location: 'الرياض, المملكة العربية السعودية'
          }
        },
        status: 'info'
      }
    ];
    
    return events;
  };

  const loadActivityFeed = async () => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const events = generateActivityEvents();
      setActivities(events);
      setLoading(false);
    }, 800);
  };

  useEffect(() => {
    loadActivityFeed();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        // In a real app, this would fetch new activities
        const newEvent = generateActivityEvents()[0];
        newEvent.id = Date.now().toString();
        newEvent.timestamp = new Date();
        
        setActivities(prev => [newEvent, ...prev.slice(0, 19)]); // Keep only 20 latest
      }, 60000); // Refresh every minute
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'upload': return <Upload className="w-4 h-4" />;
      case 'process': return <Activity className="w-4 h-4" />;
      case 'edit': return <Edit className="w-4 h-4" />;
      case 'delete': return <Trash2 className="w-4 h-4" />;
      case 'export': return <Download className="w-4 h-4" />;
      case 'login': return <User className="w-4 h-4" />;
      case 'system': return <Database className="w-4 h-4" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: 'success' | 'error' | 'warning' | 'info') => {
    switch (status) {
      case 'success': return 'text-success-600 bg-success-100';
      case 'error': return 'text-error-600 bg-error-100';
      case 'warning': return 'text-warning-600 bg-warning-100';
      case 'info': return 'text-primary-600 bg-primary-100';
    }
  };

  const getTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} يوم`;
    if (hours > 0) return `${hours} ساعة`;
    if (minutes > 0) return `${minutes} دقيقة`;
    return 'الآن';
  };

  const filteredActivities = activities.filter(activity => 
    filter === 'all' || activity.type === filter
  );

  if (loading) {
    return (
      <div className="card-gradient">
        <div className="card-header">
          <h3 className="font-bold text-gray-900">سجل النشاطات</h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-gradient"
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-600" />
            سجل النشاطات
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as ActivityType | 'all')}
              className="input text-sm"
            >
              <option value="all">جميع النشاطات</option>
              <option value="process">المعالجة</option>
              <option value="upload">الرفع</option>
              <option value="edit">التعديل</option>
              <option value="delete">الحذف</option>
              <option value="export">التصدير</option>
              <option value="system">النظام</option>
              <option value="error">الأخطاء</option>
            </select>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                autoRefresh ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {autoRefresh ? 'تحديث تلقائي' : 'تحديث يدوي'}
            </button>
            <button onClick={loadActivityFeed} className="btn-outline text-sm">
              <RefreshCw className="w-4 h-4 ml-1" />
              تحديث
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="space-y-3 max-h-96 overflow-y-auto">
          <AnimatePresence>
            {filteredActivities.length > 0 ? (
              filteredActivities.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex gap-3 p-3 hover:bg-white/60 rounded-lg transition-all duration-200"
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(activity.status)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {activity.title}
                      </h4>
                      <span className="flex items-center text-xs text-gray-500 gap-1">
                        <Clock className="w-3 h-3" />
                        منذ {getTimeAgo(activity.timestamp)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {activity.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {activity.user && (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span>{activity.user}</span>
                          </div>
                        )}
                        {activity.metadata?.entity_name && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{activity.metadata.entity_name}</span>
                          </div>
                        )}
                      </div>
                      
                      {activity.metadata?.details && (
                        <button className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
                          <span>تفاصيل</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    
                    {activity.metadata?.details && (
                      <div className="mt-2 p-2 bg-gray-50/80 rounded-lg">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(activity.metadata.details).slice(0, 4).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                              <span className="font-medium text-gray-700">
                                {typeof value === 'number' ? value.toLocaleString() : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>لا توجد نشاطات</p>
                <p className="text-sm mt-1">
                  {filter !== 'all' ? `لا توجد نشاطات من نوع "${filter}"` : 'لا توجد نشاطات حالياً'}
                </p>
              </div>
            )}
          </AnimatePresence>
        </div>
        
        {filteredActivities.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200/50 text-center">
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              عرض المزيد من النشاطات
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ActivityFeed;